import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { ExplanationAccordion, GreenText, LabelField } from 'components'
import { useAuth } from 'context'
import * as controllers from 'controllers'
import { VehicleCheckInManufacturerLabelDatesDto, VehicleDto } from 'dtos'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { formatDate } from 'utils'
import ManufacturerLabelEditDialog from './ManufacturerLabelEditDialog'

interface ManufacturerLabelCardProps {
  vehicle: VehicleDto
}

export default function ManufacturerLabelCard({ vehicle }: ManufacturerLabelCardProps) {
  const { permissions } = useAuth()

  const [isGettingManufacturerLabel, setIsGettingManufacturerLabelDates] = useState<boolean>(false)
  const [isSavingManufacturerLabelDates, setIsSavingManufacturerLabelDates] = useState<boolean>(false)
  const [manufacturerLabelDates, setManufacturerLabelDates] = useState<VehicleCheckInManufacturerLabelDatesDto>(
    new VehicleCheckInManufacturerLabelDatesDto()
  )
  const [manufacturerLabelEditDialogOpen, setManufacturerLabelEditDialogOpen] = useState<boolean>(false)

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      setIsGettingManufacturerLabelDates(true)
      controllers
        .getManufacturerLabelDateInformation(vehicle.vehicleGuid)
        .then(response => {
          setManufacturerLabelDates(response.value)
        })
        .finally(() => {
          setIsGettingManufacturerLabelDates(false)
        })
    }
  }, [vehicle])

  return (
    <>
      <ManufacturerLabelEditDialog
        initialValues={manufacturerLabelDates}
        isLoading={isSavingManufacturerLabelDates}
        onClose={() => {
          setManufacturerLabelEditDialogOpen(false)
        }}
        onSave={values => {
          setIsSavingManufacturerLabelDates(true)
          controllers
            .updateManufacturerLabelDateInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Manufacturer Label Dates Saved Successfully!', { variant: 'success' })
              setManufacturerLabelDates(response.value)
              setManufacturerLabelEditDialogOpen(false)
            })
            .finally(() => {
              setIsSavingManufacturerLabelDates(false)
            })
        }}
        open={manufacturerLabelEditDialogOpen}
      />

      <Card variant='outlined'>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={6}>
                <Typography fontWeight='bold' variant='h5'>
                  Manufacturer Label
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  disabled={
                    permissions.VEHICLE_CHECK_IN_EDIT === false ||
                    manufacturerLabelDates.vehicleCheckInIsManufacturersLabelPhotoMissing !== true
                  }
                  onClick={() => {
                    setManufacturerLabelEditDialogOpen(true)
                  }}
                  variant='text'
                  endIcon={<Edit />}
                >
                  EDIT
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingManufacturerLabel && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingManufacturerLabel && (
              <>
                <Grid item xs={12}>
                  <LabelField
                    label='Manufacturer Label Ordered'
                    value={
                      manufacturerLabelDates.manufacturerLabelOrderedDate
                        ? formatDate(manufacturerLabelDates.manufacturerLabelOrderedDate)
                        : ''
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Manufacturer Label Received'
                    value={
                      manufacturerLabelDates.manufacturerLabelReceivedDate
                        ? formatDate(manufacturerLabelDates.manufacturerLabelReceivedDate)
                        : ''
                    }
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**EDIT Button:**
                
Clicking the EDIT button opens the Manufacturer Label 
dialog.

The Manufacturer Label section is only editable if the vehicle 
has been checked in and the Manufacturer Label Missing 
button was pressed during the vehicle check-in process.

**Manufacturer Label Ordered:**

Displays the date when the Manufacturer Label was ordered, 
as entered in the Manufacturer Label dialog.

If no value has been entered for the Manufacturer Label 
Received, a “-” will be displayed.

**Manufacturer Label Received:**

Displays the date when the Manufacturer Label was 
received, as entered in the Manufacturer Label dialog.

If no value has been entered for the Manufacturer Label 
Ordered, a “-” will be displayed.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
